import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon } from '@treams/treams-icons';
import { Accordion } from '@treams/treams-ui/common/Accordion';
import { Card } from '@treams/treams-ui/common/Card';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { Heading3, Paragraph } from '@treams/treams-ui/common/Text';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { formatSalary } from '../../salary-development/formatSalary';
import {
	GENDER_GAP_GENDERS,
	type GenderGap,
	type GenderGapGender,
	type GenderGapRow,
} from './buildGenderGap';

const GENDER_COLORS: Record<GenderGapGender, string> = {
	MALE: tokens.info.icon,
	FEMALE: tokens.brand.icon,
	OTHER: tokens.warning.icon,
};

const Track = styled.div`
	flex: 1;
	min-width: 0;
	height: 8px;
	border-radius: ${tokens.radius.full}px;
	background: ${tokens.surface.secondary};
	overflow: hidden;
`;

const Fill = styled.div<{ $share: number; $gender: GenderGapGender }>`
	height: 100%;
	width: ${(props) => props.$share * 100}%;
	border-radius: ${tokens.radius.full}px;
	background: ${(props) => GENDER_COLORS[props.$gender]};
`;

const Dot = styled.span<{ $gender: GenderGapGender }>`
	display: inline-block;
	flex-shrink: 0;
	width: 8px;
	height: 8px;
	border-radius: ${tokens.radius.full}px;
	background: ${(props) => GENDER_COLORS[props.$gender]};
`;

const Amount = styled(Box)`
	flex: 0 0 140px;
	white-space: nowrap;
`;

function femaleDifference(row: GenderGapRow): number | undefined {
	const male = row.bars.find((bar) => bar.gender === 'MALE')?.average;
	const female = row.bars.find((bar) => bar.gender === 'FEMALE')?.average;

	if (male == null || female == null || male === 0) {
		return undefined;
	}

	return Math.round(((female - male) / male) * 100);
}

function Bar({
	label,
	gender,
	amount,
	max,
	text,
}: {
	label: string;
	gender: GenderGapGender;
	amount?: number;
	max: number;
	text: string;
}) {
	return (
		<FlexGap alignItems="center" gap={tokens.gap.element} py="2px">
			<Track
				role="meter"
				aria-label={label}
				aria-valuemin={0}
				aria-valuemax={Math.round(max)}
				aria-valuenow={amount == null ? undefined : Math.round(amount)}
			>
				{amount != null && max > 0 && <Fill $gender={gender} $share={amount / max} />}
			</Track>
			<Amount>
				<Paragraph small>{text}</Paragraph>
			</Amount>
		</FlexGap>
	);
}

function Chevron({ expanded }: { expanded?: boolean }) {
	return (
		<Box
			style={{
				display: 'flex',
				flexShrink: 0,
				width: 16,
				color: tokens.icon.tertiary,
				transform: expanded ? 'rotate(90deg)' : undefined,
				transition: 'transform 120ms ease-out',
			}}
		>
			{expanded != null && <ChevronRightIcon size={16} />}
		</Box>
	);
}

function Row({
	row,
	max,
	isTotal,
	expanded,
}: {
	row: GenderGapRow;
	max: number;
	isTotal?: boolean;
	expanded?: boolean;
}) {
	const { i18n, t } = useTranslation();
	const difference = femaleDifference(row);
	const Name = isTotal || expanded != null ? Heading3 : Paragraph;

	return (
		<FlexGap
			alignItems="center"
			gap={tokens.gap.list}
			py={`${tokens.padding.control}px`}
			style={isTotal ? { borderTop: `1px solid ${tokens.border.subtle}` } : undefined}
		>
			<Chevron expanded={expanded} />
			<Box flex="1 1 160px" minWidth={0} maxWidth={280}>
				<Name>{row.name}</Name>
				{difference != null && (
					<Paragraph small>
						{t('analytics.reward.genderGap.difference', {
							percent: `${difference > 0 ? '+' : ''}${difference}`,
						})}
					</Paragraph>
				)}
			</Box>
			<Box flex="2 1 220px" minWidth={0}>
				{row.bars.map((bar) => (
					<Bar
						key={bar.gender}
						label={`${row.name}: ${t(`settings.employees.personal.gender.${bar.gender}`)}`}
						gender={bar.gender}
						amount={bar.average}
						max={max}
						text={
							bar.average == null
								? '–'
								: `${formatSalary(Math.round(bar.average), i18n.language)} · ${t(
										'analytics.reward.genderGap.people',
										{ count: bar.count },
									)}`
						}
					/>
				))}
			</Box>
		</FlexGap>
	);
}

function Employees({ row, max }: { row: GenderGapRow; max: number }) {
	const { i18n, t } = useTranslation();

	return (
		<Box style={{ paddingBottom: tokens.padding.container }}>
			{row.employees.map((employee) => (
				<FlexGap
					key={employee.id}
					alignItems="center"
					gap={tokens.gap.list}
					py={`${tokens.padding.control}px`}
				>
					<Chevron />
					<FlexGap
						flex="1 1 160px"
						minWidth={0}
						maxWidth={280}
						alignItems="center"
						gap={tokens.gap.element}
						style={{ paddingLeft: tokens.gap.section }}
					>
						<Dot $gender={employee.gender} />
						<Paragraph>{employee.name}</Paragraph>
					</FlexGap>
					<Box flex="2 1 220px" minWidth={0}>
						<Bar
							label={`${employee.name}: ${t(`settings.employees.personal.gender.${employee.gender}`)}`}
							gender={employee.gender}
							amount={employee.salary}
							max={max}
							text={formatSalary(Math.round(employee.salary), i18n.language)}
						/>
					</Box>
				</FlexGap>
			))}
		</Box>
	);
}

export function GenderGapTable({ genderGap }: { genderGap: GenderGap }) {
	const { t } = useTranslation();
	const [open, setOpen] = useState<Record<string, boolean>>({});
	const max = Math.max(
		...[...genderGap.functions, genderGap.total].flatMap((row) => [
			...row.bars.map((bar) => bar.average ?? 0),
			...row.employees.map((employee) => employee.salary),
		]),
	);

	return (
		<Card>
			<Heading3>{t('analytics.reward.genderGap.title')}</Heading3>
			<Paragraph small>{t('analytics.reward.genderGap.description')}</Paragraph>
			<FlexGap mt={3} gap={tokens.gap.group} flexWrap="wrap">
				{GENDER_GAP_GENDERS.map((gender) => (
					<FlexGap key={gender} alignItems="center" gap={tokens.gap.element}>
						<Dot $gender={gender} />
						<Paragraph small>{t(`settings.employees.personal.gender.${gender}`)}</Paragraph>
					</FlexGap>
				))}
			</FlexGap>
			<FlexGap mt={3} mb={2} alignItems="center" gap={tokens.gap.list}>
				<Chevron />
				<Box flex="1 1 160px" minWidth={0} maxWidth={280}>
					<Paragraph small>{t('analytics.reward.genderGap.column.function')}</Paragraph>
				</Box>
				<Box flex="2 1 220px" minWidth={0}>
					<Paragraph small>{t('analytics.reward.genderGap.column.salary')}</Paragraph>
				</Box>
			</FlexGap>
			{genderGap.functions.map((row) => {
				const expanded = open[row.id] ?? false;

				return (
					<Accordion
						key={row.id}
						borderless
						noPadding
						isExpanded={expanded}
						onClick={() => setOpen((current) => ({ ...current, [row.id]: !expanded }))}
						customHeader={<Row row={row} max={max} expanded={expanded} />}
					>
						<Employees row={row} max={max} />
					</Accordion>
				);
			})}
			<Row row={genderGap.total} max={max} isTotal />
		</Card>
	);
}
